/**
 * =============================================================================
 * ActivityPhaseList
 * Versão: 2.0.0
 *
 * Lista compacta de atividades (steps de cadência) de uma fase específica
 * do negócio.
 *
 * Changelog:
 *  - 2.0.0: Refatoração visual completa. Remove o grid tabelar de 4 colunas
 *           (Data | Atividade | Concl. | Ações) e adota linha-card com:
 *           chip circular colorido por tipo de atividade, título + metadata
 *           (data · tipo) em duas linhas, checkbox à direita e menu ⋮
 *           revelado no hover. Alinha o visual com o padrão dos blocos do
 *           sidebar direito do modal de negócios.
 *  - 1.3.0: Adiciona item "Reabrir atividade" no menu 3-pontinhos quando o
 *           passo está concluído/pulado, via nova prop opcional `onReopen`.
 *  - 1.2.0: Substitui o Popover por linha por um Dialog ÚNICO no nível do
 *           componente para o fluxo "Reagendar".
 *  - 1.1.0: Substitui a coluna "Pular" por menu de 3 pontinhos com ações.
 *  - 1.0.0: Versão inicial com colunas Data | Atividade | Concl. | Pular.
 * =============================================================================
 */
import React, { useState } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Phone, Mail, MessageSquare, Calendar as CalendarIcon, CheckSquare, StickyNote, MapPin,
  ListChecks, SkipForward, MoreVertical, CheckCircle2, CalendarClock, RotateCcw,
  Ban, PencilLine,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Calendar } from '@/components/ui/calendar';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import type { CadenceStep } from '@/hooks/useDealCadence';

// Ícone por tipo de atividade
const activityIcon: Record<string, typeof Phone> = {
  call: Phone,
  email: Mail,
  whatsapp: MessageSquare,
  meeting: CalendarIcon,
  task: CheckSquare,
  note: StickyNote,
  visit: MapPin,
};

// Rótulo legível por tipo (usado na metadata secundária)
const activityLabel: Record<string, string> = {
  call: 'Ligação',
  email: 'E-mail',
  whatsapp: 'WhatsApp',
  meeting: 'Reunião',
  task: 'Tarefa',
  note: 'Nota',
  visit: 'Visita',
};

/**
 * Paleta discreta por tipo de atividade para o chip circular do ícone.
 * Cores usam tokens Tailwind com opacidade — mantém coerência visual em
 * dark/light sem hardcodar hex.
 */
const activityTint: Record<string, { bg: string; text: string }> = {
  call:     { bg: 'bg-emerald-500/10', text: 'text-emerald-600' },
  whatsapp: { bg: 'bg-green-500/10',   text: 'text-green-600'   },
  email:    { bg: 'bg-blue-500/10',    text: 'text-blue-600'    },
  meeting:  { bg: 'bg-violet-500/10',  text: 'text-violet-600'  },
  task:     { bg: 'bg-amber-500/10',   text: 'text-amber-600'   },
  note:     { bg: 'bg-slate-500/10',   text: 'text-slate-600'   },
  visit:    { bg: 'bg-rose-500/10',    text: 'text-rose-600'    },
};

interface ActivityPhaseListProps {
  steps: CadenceStep[];
  /** Fase selecionada — usada para filtro client-side. Quando null, mostra todos. */
  phaseId: string | null;
  selectedStepId: string | null;
  onSelectStep: (stepId: string) => void;
  onToggleComplete: (stepId: string, currentlyDone: boolean) => void;
  /** Callback para pular atividade (skip) */
  onSkip?: (stepId: string) => void;
  /** Callback para reagendar (recebe ISO string com data+hora) */
  onReschedule?: (stepId: string, isoDateTime: string) => void;
  /** Callback para reabrir uma atividade concluída ou pulada */
  onReopen?: (stepId: string) => void;
  /** [v2.1.0] Cancelar step com justificativa (motivo é obrigatório, mín. 5 chars) */
  onCancel?: (stepId: string, reason: string) => void;
  /** [v2.1.0] Ajustar historicamente a data/hora de conclusão de um step concluído */
  onEditCompletionDate?: (stepId: string, isoDateTime: string) => void;
  /** [v2.2.0] Editar atividade manual pendente (abre modal no pai) */
  onEditManualStep?: (step: CadenceStep) => void;
  /** [v2.2.0] Permite exibir o item "Editar atividade" no menu ⋮ (steps manuais) */
  canEditManualStep?: boolean;
  /** [v2.1.0] Permite exibir o item "Cancelar atividade" no menu ⋮ */
  canCancelStep?: boolean;
  /** Bloqueia toggle quando false (sem permissão de edição na fase) */
  canComplete?: boolean;
  isBusy?: boolean;
  /** Quando true, remove o scroll interno (lista flui na altura natural — usado no mobile). */
  disableInternalScroll?: boolean;
}

// Converte um ISO em Date local (ou hoje se vazio).
function toDate(iso?: string | null): Date {
  return iso ? new Date(iso) : new Date();
}

// Extrai "HH:mm" de um Date.
function toTimeString(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// Combina Date (ano/mês/dia) + "HH:mm" em um novo Date local.
function mergeDateTime(date: Date, time: string): Date {
  const [h, m] = time.split(':').map((v) => parseInt(v, 10) || 0);
  const out = new Date(date);
  out.setHours(h, m, 0, 0);
  return out;
}

export function ActivityPhaseList({
  steps,
  phaseId,
  selectedStepId,
  onSelectStep,
  onToggleComplete,
  onSkip,
  onReschedule,
  onReopen,
  onCancel,
  onEditCompletionDate,
  onEditManualStep,
  canEditManualStep = false,
  canCancelStep = false,
  canComplete = true,
  isBusy = false,
  disableInternalScroll = false,
}: ActivityPhaseListProps) {
  // Filtra por fase quando o playbook é per_phase.
  const filtered = phaseId
    ? steps.filter((s) => s.phase_id === phaseId || s.phase_id === null)
    : steps;

  // Estado único de reagendamento (Dialog no nível do componente).
  const [reschedulingStepId, setReschedulingStepId] = useState<string | null>(null);
  const [rescheduleDate, setRescheduleDate] = useState<Date | undefined>(undefined);
  const [rescheduleTime, setRescheduleTime] = useState<string>('09:00');

  // [v2.1.0] Estado para cancelar step com justificativa obrigatória.
  const [cancellingStepId, setCancellingStepId] = useState<string | null>(null);
  const [cancelReason, setCancelReason] = useState<string>('');

  // [v2.1.0] Estado para editar data de conclusão de step já concluído.
  const [editingCompletionStepId, setEditingCompletionStepId] = useState<string | null>(null);
  const [completionDate, setCompletionDate] = useState<Date | undefined>(undefined);
  const [completionTime, setCompletionTime] = useState<string>('09:00');

  const openReschedule = (step: CadenceStep) => {
    const initial = toDate(step.due_at);
    setRescheduleDate(initial);
    setRescheduleTime(toTimeString(initial));
    setReschedulingStepId(step.step_id);
  };
  const closeReschedule = () => setReschedulingStepId(null);

  const openCancel = (step: CadenceStep) => {
    setCancelReason('');
    setCancellingStepId(step.step_id);
  };
  const closeCancel = () => {
    setCancellingStepId(null);
    setCancelReason('');
  };

  const openEditCompletion = (step: CadenceStep) => {
    const initial = toDate(step.completed_at ?? step.due_at);
    setCompletionDate(initial);
    setCompletionTime(toTimeString(initial));
    setEditingCompletionStepId(step.step_id);
  };
  const closeEditCompletion = () => setEditingCompletionStepId(null);

  if (!filtered.length) {
    return (
      <div className="flex flex-col items-center justify-center py-10 text-center text-muted-foreground">
        <ListChecks className="h-8 w-8 mb-2 opacity-40" />
        <p className="text-xs">Nenhuma atividade nesta fase.</p>
      </div>
    );
  }

  return (
    <div className={disableInternalScroll ? '' : 'overflow-y-auto scrollbar-thin'}>
      {/* Lista fluida (sem grade tabelar). Cada linha é um "card" com
          respiro vertical, sem borda dura entre linhas. */}
      <ul className="flex flex-col gap-0.5 p-1.5">
        {filtered.map((step) => {
          const Icon = activityIcon[step.activity_type] ?? ListChecks;
          const tint = activityTint[step.activity_type] ?? { bg: 'bg-muted', text: 'text-muted-foreground' };
          const isDone = step.step_status === 'completed';
          const isSkipped = step.step_status === 'skipped';
          const isCancelled = step.step_status === 'cancelled';
          const isSelected = step.step_id === selectedStepId;
          const isLocked = isDone || isSkipped || isCancelled;
          const dueLabel = step.due_at
            ? format(new Date(step.due_at), "dd MMM", { locale: ptBR })
            : '—';
          const typeLabel = activityLabel[step.activity_type] ?? 'Atividade';

          return (
            <li
              key={step.step_id}
              className={cn(
                'group relative flex items-center gap-3 px-2.5 py-2 rounded-md cursor-pointer transition-colors',
                'hover:bg-muted/50',
                isSelected && 'bg-primary/5',
                isLocked && 'opacity-70',
              )}
              onClick={() => onSelectStep(step.step_id)}
              title={isCancelled && step.cancelled_reason ? `Cancelado: ${step.cancelled_reason}` : undefined}
            >
              {/* Barra lateral de seleção */}
              {isSelected && (
                <span className="absolute left-0 top-1.5 bottom-1.5 w-0.5 bg-primary rounded-r" />
              )}

              {/* Checkbox de conclusão (movido para o início) */}
              <div className="flex items-center shrink-0" onClick={(e) => e.stopPropagation()}>
                <Checkbox
                  checked={isDone}
                  disabled={!canComplete || isBusy || isSkipped || isCancelled}
                  onCheckedChange={() => {
                    onToggleComplete(step.step_id, isDone);
                    onSelectStep(step.step_id);
                  }}
                  aria-label="Concluir atividade"
                  className="data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600"
                />
              </div>

              {/* Menu de ações "⋮" */}
              <div
                className={cn(
                  'shrink-0 transition-opacity',
                  'opacity-0 group-hover:opacity-100 focus-within:opacity-100',
                  (isSelected || (isLocked && onReopen)) && 'opacity-100',
                )}
                onClick={(e) => e.stopPropagation()}
              >
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-muted-foreground hover:text-foreground"
                      disabled={!canComplete || isBusy || (isLocked && !onReopen && !(isDone && onEditCompletionDate))}
                      aria-label="Mais ações"
                    >
                      <MoreVertical className="h-3.5 w-3.5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-52">
                    <DropdownMenuItem
                      onClick={() => onToggleComplete(step.step_id, false)}
                      disabled={isLocked}
                      className="text-xs"
                    >
                      <CheckCircle2 className="h-3.5 w-3.5 mr-2 text-emerald-600" />
                      Concluir
                    </DropdownMenuItem>

                    <DropdownMenuItem
                      onSelect={() => {
                        requestAnimationFrame(() => openReschedule(step));
                      }}
                      disabled={isLocked || !onReschedule}
                      className="text-xs"
                    >
                      <CalendarClock className="h-3.5 w-3.5 mr-2 text-blue-600" />
                      Reagendar
                    </DropdownMenuItem>

                    <DropdownMenuSeparator />

                    <DropdownMenuItem
                      onClick={() => onSkip?.(step.step_id)}
                      disabled={isLocked || !onSkip}
                      className="text-xs text-amber-700 focus:text-amber-700"
                    >
                      <SkipForward className="h-3.5 w-3.5 mr-2" />
                      Pular
                    </DropdownMenuItem>

                    {/* [v2.2.0] Editar atividade — só para steps manuais pendentes */}
                    {step.is_manual && onEditManualStep && canEditManualStep && !isLocked && (
                      <DropdownMenuItem
                        onSelect={() => {
                          requestAnimationFrame(() => onEditManualStep(step));
                        }}
                        className="text-xs"
                      >
                        <PencilLine className="h-3.5 w-3.5 mr-2 text-amber-600" />
                        Editar atividade
                      </DropdownMenuItem>
                    )}

                    {/* [v2.1.0] Cancelar atividade — só aparece se permitido pelo playbook */}
                    {onCancel && canCancelStep && (
                      <DropdownMenuItem
                        onSelect={() => {
                          requestAnimationFrame(() => openCancel(step));
                        }}
                        disabled={isLocked}
                        className="text-xs text-destructive focus:text-destructive"
                      >
                        <Ban className="h-3.5 w-3.5 mr-2" />
                        Cancelar atividade
                      </DropdownMenuItem>
                    )}

                    {/* [v2.1.0] Editar data de conclusão — só quando concluída */}
                    {isDone && onEditCompletionDate && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onSelect={() => {
                            requestAnimationFrame(() => openEditCompletion(step));
                          }}
                          className="text-xs"
                        >
                          <PencilLine className="h-3.5 w-3.5 mr-2 text-blue-600" />
                          Editar data de conclusão
                        </DropdownMenuItem>
                      </>
                    )}

                    {isLocked && onReopen && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => onReopen(step.step_id)}
                          className="text-xs text-primary focus:text-primary"
                        >
                          <RotateCcw className="h-3.5 w-3.5 mr-2" />
                          Reabrir atividade
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              {/* Chip circular do ícone */}
              <div
                className={cn(
                  'h-9 w-9 rounded-full flex items-center justify-center shrink-0',
                  tint.bg,
                  isLocked && 'grayscale',
                )}
              >
                <Icon className={cn('h-4 w-4', tint.text)} />
              </div>

              {/* Título + metadata */}
              <div className="flex-1 min-w-0">
                <p
                  className={cn(
                    'text-[13px] font-medium leading-tight truncate',
                    isLocked && 'line-through text-muted-foreground',
                  )}
                  title={step.title}
                >
                  {step.title}
                  {step.is_manual && (
                    <span className="ml-1.5 text-[9px] uppercase tracking-wide text-muted-foreground/70 font-normal">
                      manual
                    </span>
                  )}
                </p>
                <p className="text-[11px] text-muted-foreground mt-0.5 truncate">
                  <span className="capitalize">{dueLabel}</span>
                  <span className="mx-1.5 opacity-50">·</span>
                  <span>{typeLabel}</span>
                  {isCancelled && (
                    <>
                      <span className="mx-1.5 opacity-50">·</span>
                      <span className="text-destructive">Cancelada</span>
                    </>
                  )}
                </p>
              </div>
            </li>
          );
        })}
      </ul>

      {/* Dialog ÚNICO de reagendamento */}
      <Dialog
        open={reschedulingStepId !== null}
        onOpenChange={(open) => {
          if (!open) closeReschedule();
        }}
      >
        <DialogContent className="sm:max-w-[360px] p-4">
          <DialogHeader>
            <DialogTitle className="text-sm">Reagendar atividade</DialogTitle>
          </DialogHeader>

          <Label className="text-xs font-semibold mb-1 block">
            Nova data
          </Label>
          <Calendar
            mode="single"
            locale={ptBR}
            selected={rescheduleDate}
            onSelect={(d) => setRescheduleDate(d ?? undefined)}
            initialFocus
            className={cn('p-0 pointer-events-auto')}
          />

          <div className="flex items-center gap-2 mt-3">
            <Label className="text-xs text-muted-foreground shrink-0">
              Hora
            </Label>
            <Input
              type="time"
              value={rescheduleTime}
              onChange={(e) => setRescheduleTime(e.target.value)}
              className="h-8 text-xs"
            />
          </div>

          <DialogFooter className="mt-3 gap-2 sm:gap-2">
            <Button
              size="sm"
              variant="ghost"
              className="h-8 text-xs"
              onClick={closeReschedule}
            >
              Cancelar
            </Button>
            <Button
              size="sm"
              className="h-8 text-xs"
              disabled={!rescheduleDate || !rescheduleTime || !onReschedule || !reschedulingStepId}
              onClick={() => {
                if (!rescheduleDate || !rescheduleTime || !onReschedule || !reschedulingStepId) return;
                const merged = mergeDateTime(rescheduleDate, rescheduleTime);
                onReschedule(reschedulingStepId, merged.toISOString());
                closeReschedule();
              }}
            >
              Confirmar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* [v2.1.0] Dialog de cancelamento com justificativa obrigatória */}
      <Dialog
        open={cancellingStepId !== null}
        onOpenChange={(open) => { if (!open) closeCancel(); }}
      >
        <DialogContent className="sm:max-w-[420px] p-4">
          <DialogHeader>
            <DialogTitle className="text-sm">Cancelar atividade</DialogTitle>
            <DialogDescription className="text-xs">
              Informe o motivo do cancelamento. Esta ação preserva o histórico da atividade.
            </DialogDescription>
          </DialogHeader>
          <Label className="text-xs font-semibold mb-1 block">
            Motivo <span className="text-destructive">*</span>
          </Label>
          <Textarea
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            placeholder="Explique brevemente o motivo (mín. 5 caracteres)"
            rows={4}
            className="text-xs"
            maxLength={500}
          />
          <p className="text-[10px] text-muted-foreground mt-1">
            {cancelReason.trim().length}/500 · mínimo 5 caracteres
          </p>
          <DialogFooter className="mt-3 gap-2 sm:gap-2">
            <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={closeCancel}>
              Voltar
            </Button>
            <Button
              size="sm"
              variant="destructive"
              className="h-8 text-xs"
              disabled={cancelReason.trim().length < 5 || !onCancel || !cancellingStepId}
              onClick={() => {
                if (!onCancel || !cancellingStepId) return;
                onCancel(cancellingStepId, cancelReason.trim());
                closeCancel();
              }}
            >
              Cancelar atividade
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* [v2.1.0] Dialog para editar data de conclusão (histórico real) */}
      <Dialog
        open={editingCompletionStepId !== null}
        onOpenChange={(open) => { if (!open) closeEditCompletion(); }}
      >
        <DialogContent className="sm:max-w-[360px] p-4">
          <DialogHeader>
            <DialogTitle className="text-sm">Editar data de conclusão</DialogTitle>
            <DialogDescription className="text-xs">
              Ajuste a data/hora em que a atividade foi realmente concluída.
            </DialogDescription>
          </DialogHeader>
          <Calendar
            mode="single"
            locale={ptBR}
            selected={completionDate}
            onSelect={(d) => setCompletionDate(d ?? undefined)}
            initialFocus
            className={cn('p-0 pointer-events-auto')}
          />
          <div className="flex items-center gap-2 mt-3">
            <Label className="text-xs text-muted-foreground shrink-0">Hora</Label>
            <Input
              type="time"
              value={completionTime}
              onChange={(e) => setCompletionTime(e.target.value)}
              className="h-8 text-xs"
            />
          </div>
          <DialogFooter className="mt-3 gap-2 sm:gap-2">
            <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={closeEditCompletion}>
              Cancelar
            </Button>
            <Button
              size="sm"
              className="h-8 text-xs"
              disabled={!completionDate || !completionTime || !onEditCompletionDate || !editingCompletionStepId}
              onClick={() => {
                if (!completionDate || !completionTime || !onEditCompletionDate || !editingCompletionStepId) return;
                const merged = mergeDateTime(completionDate, completionTime);
                onEditCompletionDate(editingCompletionStepId, merged.toISOString());
                closeEditCompletion();
              }}
            >
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
